// Stampa diretta dal browser verso stampanti Epson tramite il protocollo
// ufficiale ePOS-Print (HTTP/HTTPS). Nessun agente ne driver: il dispositivo
// (tablet/telefono/PC) invia lo scontrino direttamente all'IP della stampante.
//
// Endpoint Epson: {protocol}://{ip}[:{port}]/cgi-bin/epos/service.cgi
//
// Nota: se l'app e' servita in HTTPS, la stampante deve rispondere in HTTPS
// (porta 443). La prima volta occorre aprire https://IP-STAMPANTE nel browser
// e accettare il certificato della stampante (autofirmato).

import { receiptLabels } from './receiptLabels';

const EPOS_NS = 'http://www.epson-pos.com/schemas/2011/03/epos-print';

const xmlEsc = (s) =>
  String(s == null ? '' : s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

const eur = (n) => `\u20ac ${Number(n || 0).toFixed(2)}`;

const localeMap = { it: 'it-IT', es: 'es-ES', en: 'en-GB', fr: 'fr-FR', pt: 'pt-PT' };
const fmtDate = (iso, lang) => {
  if (iso === undefined || iso === null || iso === '') return '';
  const d = new Date(iso);
  // Se la stringa è già stata formattata dal backend (es. "13/07/2026 14:30"),
  // new Date() non la riconosce: in tal caso la usiamo così com'è (niente "Invalid Date").
  if (Number.isNaN(d.getTime())) return String(iso);
  try {
    return d.toLocaleString(localeMap[(lang || 'it').toLowerCase()] || 'it-IT', {
      day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch { return String(iso); }
};

// Numero di colonne carattere per la larghezza carta (font A Epson).
const colsFor = (paperWidth) => (Number(paperWidthOf(paperWidth)) === 58 ? 32 : 48);
function paperWidthOf(w) { return Number(w) === 58 ? 58 : 80; }

/** Riga sinistra/destra con riempimento a spazi per allineare gli importi. */
function leftRight(left, right, cols) {
  left = String(left || '');
  right = String(right || '');
  const space = cols - left.length - right.length;
  if (space >= 1) return left + ' '.repeat(space) + right;
  // Tronca la parte sinistra se troppo lunga.
  const maxLeft = Math.max(0, cols - right.length - 1);
  return left.slice(0, maxLeft) + ' ' + right;
}

/** Un blocco <text> con stato di formato deterministico (sempre esplicito).
 * `w`/`h` (1-8) permettono di scalare larghezza/altezza del carattere. */
function T(text, { align = 'left', em = false, dw = false, dh = false, w, h } = {}) {
  const wAttr = (w && Number(w) > 1) ? ` width="${Number(w)}"` : '';
  const hAttr = (h && Number(h) > 1) ? ` height="${Number(h)}"` : '';
  return `<text align="${align}" em="${em ? 'true' : 'false'}" dw="${dw ? 'true' : 'false'}" dh="${dh ? 'true' : 'false'}"${wAttr}${hAttr}>${xmlEsc(text)}\n</text>`;
}

// --- Lunghezza minima scontrino ------------------------------------------
// Stima l'altezza del contenuto e restituisce le righe di avanzamento carta
// necessarie affinché ogni scontrino (anche con un solo prodotto) sia lungo
// almeno ~50mm prima del taglio.
const MM_PER_LINE = 4;         // stima altezza di una riga font A (height=1)
const MIN_RECEIPT_MM = 50;
function estimateBodyMm(body) {
  let mm = 0; let m;
  const textRe = /<text\b([^>]*)>/g;
  while ((m = textRe.exec(body))) {
    const a = m[1];
    const hM = /height="(\d+)"/.exec(a);
    const h = hM ? Number(hM[1]) : (/dh="true"/.test(a) ? 2 : 1);
    mm += h * MM_PER_LINE;
  }
  const feedRe = /<feed line="(\d+)"\s*\/>/g;
  while ((m = feedRe.exec(body))) mm += Number(m[1]) * MM_PER_LINE;
  if (/<symbol/.test(body)) mm += 30;   // QR code (~30mm)
  if (/<image/.test(body)) mm += 20;    // logo raster (~20mm)
  return mm;
}
/** Righe di feed finali: garantisce min 50mm e un minimo di margine per il taglio. */
function tailFeedLines(body, clearance = 3) {
  const needed = Math.ceil((MIN_RECEIPT_MM - estimateBodyMm(body)) / MM_PER_LINE);
  return Math.max(clearance, needed);
}

/** Carica un'immagine (data URL o URL) restituendo una Promise<HTMLImageElement>. */
function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Impossibile caricare il logo'));
    img.src = src;
  });
}

/**
 * Converte un logo (data URL base64) in un elemento <image> ePOS-Print
 * raster monocromatico (1 bit/pixel, righe allineate al byte, MSB-first).
 * Usa il dithering Floyd-Steinberg per una resa migliore.
 * Ritorna stringa vuota se il logo non è valido (per non bloccare la stampa).
 */
export async function logoToEposXml(dataUrl, paperWidth = 80, opts = {}) {
  if (!dataUrl || typeof dataUrl !== 'string') return '';
  if (typeof document === 'undefined' || typeof btoa === 'undefined') return '';
  const printable = Number(paperWidthOf(paperWidth)) === 58 ? 384 : 576;
  const maxWidth = Math.min(opts.maxWidth || printable, printable);
  const maxHeight = opts.maxHeight || 900;
  let img;
  try { img = await loadImage(dataUrl); } catch { return ''; }
  const sw0 = img.naturalWidth || img.width;
  const sh0 = img.naturalHeight || img.height;
  if (!sw0 || !sh0) return '';

  // Rifila automaticamente i bordi bianchi/trasparenti attorno al logo, così
  // non lascia spazi vuoti sopra/sotto sullo scontrino.
  let crop = { x: 0, y: 0, w: sw0, h: sh0 };
  try {
    const tc = document.createElement('canvas');
    tc.width = sw0; tc.height = sh0;
    const tctx = tc.getContext('2d');
    tctx.drawImage(img, 0, 0);
    const td = tctx.getImageData(0, 0, sw0, sh0).data;
    let top = sh0, left = sw0, right = -1, bottom = -1;
    for (let y = 0; y < sh0; y++) {
      for (let x = 0; x < sw0; x++) {
        const i = (y * sw0 + x) * 4;
        const a = td[i + 3];
        const lum = 0.299 * td[i] + 0.587 * td[i + 1] + 0.114 * td[i + 2];
        if (a > 16 && lum < 245) { // pixel "di contenuto" (non bianco, non trasparente)
          if (x < left) left = x; if (x > right) right = x;
          if (y < top) top = y; if (y > bottom) bottom = y;
        }
      }
    }
    if (right >= left && bottom >= top) {
      const pad = Math.round(Math.max(sw0, sh0) * 0.01);
      left = Math.max(0, left - pad); top = Math.max(0, top - pad);
      right = Math.min(sw0 - 1, right + pad); bottom = Math.min(sh0 - 1, bottom + pad);
      crop = { x: left, y: top, w: right - left + 1, h: bottom - top + 1 };
    }
  } catch { /* nessun trim disponibile */ }

  const sw = crop.w, sh = crop.h;
  // Rispetta la dimensione già scelta dall'utente: non ingrandire mai (scale <= 1),
  // riduci solo se il logo eccede la larghezza/altezza stampabile.
  const scale = Math.min(maxWidth / sw, maxHeight / sh, 1);
  const dw = Math.max(1, Math.round(sw * scale));
  const dh = Math.max(1, Math.round(sh * scale));

  let canvas;
  try {
    canvas = document.createElement('canvas');
    canvas.width = dw;
    canvas.height = dh;
  } catch { return ''; }
  const ctx = canvas.getContext('2d');
  if (!ctx) return '';
  ctx.fillStyle = '#ffffff'; // sfondo bianco per gestire la trasparenza
  ctx.fillRect(0, 0, dw, dh);
  ctx.drawImage(img, crop.x, crop.y, crop.w, crop.h, 0, 0, dw, dh);
  let px;
  try { px = ctx.getImageData(0, 0, dw, dh).data; } catch { return ''; }

  // Scala di grigi (la trasparenza diventa bianco).
  const gray = new Float32Array(dw * dh);
  for (let i = 0, p = 0; i < gray.length; i++, p += 4) {
    const a = px[p + 3] / 255;
    const r = px[p], g = px[p + 1], b = px[p + 2];
    const lum = 0.299 * r + 0.587 * g + 0.114 * b;
    gray[i] = lum * a + 255 * (1 - a);
  }

  // Dithering Floyd-Steinberg.
  const bytesPerLine = Math.ceil(dw / 8);
  const raster = new Uint8Array(bytesPerLine * dh);
  for (let y = 0; y < dh; y++) {
    for (let x = 0; x < dw; x++) {
      const idx = y * dw + x;
      const oldVal = gray[idx];
      const newVal = oldVal < 128 ? 0 : 255;
      const err = oldVal - newVal;
      if (newVal === 0) {
        raster[y * bytesPerLine + (x >> 3)] |= 0x80 >> (x & 7); // bit 1 = punto nero
      }
      if (x + 1 < dw) gray[idx + 1] += (err * 7) / 16;
      if (y + 1 < dh) {
        if (x > 0) gray[idx + dw - 1] += (err * 3) / 16;
        gray[idx + dw] += (err * 5) / 16;
        if (x + 1 < dw) gray[idx + dw + 1] += (err * 1) / 16;
      }
    }
  }

  let bin = '';
  for (let i = 0; i < raster.length; i++) bin += String.fromCharCode(raster[i]);
  const b64 = btoa(bin);
  return `<image width="${dw}" height="${dh}" align="center" color="color_1" mode="mono">${b64}</image>`;
}

/** Costruisce il corpo ePOS-Print (XML) a partire dai dati ricevuta. */
export function buildEposXml(data, paperWidth = 80, logoXml = '') {
  const cols = colsFor(paperWidth);
  const r = data.restaurant || {};
  const t = data.template || {};
  const o = data.order || {};
  const L = data.labels || receiptLabels(data.lang);
  const sep = '-'.repeat(cols);
  const parts = [];

  // Logo (raster monocromatico) in cima allo scontrino, se disponibile.
  if (logoXml) {
    parts.push(logoXml);
    parts.push(`<feed line="1"/>`);
  }

  // Intestazione
  if (r.name) parts.push(T(r.name, { align: 'center', em: true, dh: true }));
  const head = [];
  if ((t.show_address === undefined || t.show_address) && r.address) head.push(r.address);
  if ((t.show_address === undefined || t.show_address) && r.address_line2) head.push(r.address_line2);
  if ((t.show_phone === undefined || t.show_phone) && r.phone) head.push(`${L.phone_label || 'Tel'}: ${r.phone}`);
  if ((t.show_vat === undefined || t.show_vat) && r.vat) head.push(`${L.vat_label || 'P.IVA'} ${r.vat}`);
  (t.header_lines || []).filter(Boolean).forEach((l) => head.push(l));
  head.forEach((l) => parts.push(T(l, { align: 'center' })));

  parts.push(T(sep));
  if (data.title) parts.push(T(String(data.title).toUpperCase(), { align: 'center', em: true }));
  if (data.is_fiscal === false) parts.push(T(L.non_fiscal || 'DOCUMENTO NON FISCALE', { align: 'center' }));

  // Meta (tavolo / data / cameriere)
  const covers = o.covers ? `${L.covers || 'Coperti'}: ${o.covers}` : '';
  if (o.table_name || covers) parts.push(T(leftRight(o.table_name || '', covers, cols)));
  parts.push(T(leftRight(fmtDate(data.datetime_iso || data.datetime, data.lang), o.waiter || '', cols)));
  parts.push(T(sep));

  // Righe articoli
  (data.lines || []).forEach((it) => {
    parts.push(T(leftRight(`${it.qty}x ${it.name}${it.variant ? ` (${it.variant})` : ''}`, eur(it.line_total), cols)));
    (it.extras || []).forEach((e) => parts.push(T(`  + ${e.name}${e.price ? `  ${eur(e.price)}` : ''}`)));
    if (it.notes) parts.push(T(`  \u00bb ${it.notes}`));
  });

  parts.push(T(sep));
  parts.push(T(leftRight(L.subtotal || 'Subtotale', eur(data.items_total), cols)));
  if (Number(data.discount || 0) > 0) parts.push(T(leftRight(`${L.discount || 'Sconto'}${(data.discount_label || data.discount_code) ? ` (${data.discount_label || data.discount_code})` : ''}`, `- ${eur(data.discount)}`, cols)));
  parts.push(T(leftRight(L.total || 'TOTALE', eur(data.total), cols), { em: true, dh: true }));
  const pay = data.payment || {};
  if (pay.rows && pay.rows.length) {
    parts.push(T(sep));
    parts.push(T(String(L.payment || 'Pagamento').toUpperCase()));
    pay.rows.forEach((row) => parts.push(T(leftRight(row.label, eur(row.amount), cols))));
  }
  parts.push(T(sep));
  parts.push(T(leftRight(L.taxable || 'Imponibile', eur(data.imponibile), cols)));
  const vatTxt = String(L.vat_included || 'IVA {rate}% inclusa').replace('{rate}', Number(data.vat_rate || 0).toFixed(0));
  parts.push(T(leftRight(vatTxt, eur(data.iva), cols)));

  // Piè di pagina
  (t.footer_lines || []).filter(Boolean).forEach((l) => parts.push(T(l, { align: 'center' })));

  // QR (link copia PDF) se disponibile
  if (data.pdf_url) {
    parts.push(`<feed line="1"/>`);
    parts.push(`<symbol type="qrcode_model_2" level="level_m" width="5" height="5" size="0">${xmlEsc(data.pdf_url)}</symbol>`);
    parts.push(`<feed line="1"/>`);
    parts.push(T(L.qr_hint || '', { align: 'center' }));
  }

  const body = parts.join('');
  return `<?xml version="1.0" encoding="utf-8"?>
<s:Envelope xmlns:s="http://schemas.xmlsoap.org/soap/envelope/"><s:Body>
<epos-print xmlns="${EPOS_NS}">
${body}
<feed line="${tailFeedLines(body)}"/>
<cut type="feed"/>
${data.open_drawer ? '<pulse drawer="drawer_1" time="pulse_100"/>' : ''}
</epos-print>
</s:Body></s:Envelope>`;
}

/**
 * Costruisce lo scontrino-TESSERA (fedeltà o gift card) in formato verticale:
 * logo in alto → titolo → nome cliente → valore grande → codice → QR → footer.
 * `card` = { businessName, title, lines[], valueLabel, valueText, code, qrUrl, footer }
 */
export function buildCardEposXml(card, paperWidth = 80, logoXml = '') {
  const cols = colsFor(paperWidth);
  const sep = '-'.repeat(cols);
  const parts = [];
  if (logoXml) { parts.push(logoXml); parts.push('<feed line="1"/>'); }
  if (card.businessName) parts.push(T(card.businessName, { align: 'center', em: true, dh: true }));
  parts.push(T(sep));
  if (card.title) parts.push(T(String(card.title).toUpperCase(), { align: 'center', em: true }));
  parts.push('<feed line="1"/>');
  (card.lines || []).filter(Boolean).forEach((l) => parts.push(T(l, { align: 'center' })));
  if (card.valueText != null && card.valueText !== '') {
    parts.push('<feed line="1"/>');
    if (card.valueLabel) parts.push(T(card.valueLabel, { align: 'center' }));
    parts.push(T(String(card.valueText), { align: 'center', em: true, dw: true, dh: true }));
  }
  if (card.code) { parts.push('<feed line="1"/>'); parts.push(T(card.code, { align: 'center', em: true })); }
  if (card.qrUrl) {
    parts.push('<feed line="1"/>');
    parts.push(`<symbol type="qrcode_model_2" level="level_m" width="6" height="6" size="0">${xmlEsc(card.qrUrl)}</symbol>`);
    parts.push('<feed line="1"/>');
    parts.push(T(card.qrHint || 'Inquadra il QR per i tuoi dati', { align: 'center' }));
  }
  parts.push(T(sep));
  if (card.footer) parts.push(T(card.footer, { align: 'center' }));
  const body = parts.join('');
  return `<?xml version="1.0" encoding="utf-8"?>
<s:Envelope xmlns:s="http://schemas.xmlsoap.org/soap/envelope/"><s:Body>
<epos-print xmlns="${EPOS_NS}">
${body}
<feed line="${tailFeedLines(body)}"/>
<cut type="feed"/>
</epos-print>
</s:Body></s:Envelope>`;
}

/** Stampa una tessera (fedeltà/gift) via ePOS diretto. */
export async function eposPrintCard(cfg, card) {
  const logoXml = card.logo ? await logoToEposXml(card.logo, cfg.paper_width) : '';
  const xml = buildCardEposXml(card, cfg.paper_width, logoXml);
  return sendEpos(cfg, xml);
}

/** Costruisce l'XML ePOS-Print di una COMANDA di reparto (cucina/bar). */
export function buildComandaEposXml(data, paperWidth = 80) {
  const cols = colsFor(paperWidth);
  const L = data.labels || receiptLabels(data.lang);
  const o = data.order || {};
  const sep = '-'.repeat(cols);
  let stationLabel;
  if (data.station === 'bar') stationLabel = (L.bar || 'BAR');
  else if (data.station === 'cameriere') stationLabel = (L.waiter || 'CAMERIERE');
  else stationLabel = (L.kitchen || 'CUCINA');
  stationLabel = String(stationLabel).toUpperCase();
  const parts = [];
  // Scontrino di STORNO/ANULACIÓN: banner in evidenza in cima.
  if (data.storno) {
    parts.push(T(String(data.title || 'ANULACION').toUpperCase(), { align: 'center', em: true, h: 3, w: 3 }));
    if (data.storno_hint) parts.push(T(String(data.storno_hint).toUpperCase(), { align: 'center', em: true, h: 2 }));
    if (data.storno_reason) parts.push(T(`Motivo: ${data.storno_reason}`, { align: 'center', h: 2 }));
    if (data.storno_by) parts.push(T(`Operador: ${data.storno_by}`, { align: 'center', h: 2 }));
    parts.push(T(sep));
  }
  // Testo comanda ingrandito (~+50%): postazione e prodotti height 3, resto height 2.
  parts.push(T(`** ${stationLabel} **`, { align: 'center', em: true, h: 3 }));
  // Un po' di spazio tra il nome postazione e il numero tavolo.
  parts.push(`<feed line="1"/>`);
  if (o.table_name) parts.push(T(o.table_name, { align: 'center', em: true, h: 3 }));
  // Data e ora: preferiamo l'ISO grezzo (`datetime_iso`) così il DISPOSITIVO che
  // stampa lo converte nell'ora LOCALE. In mancanza, ripiega sulla stringa già
  // formattata dal backend (fmtDate la usa verbatim).
  const dt = fmtDate(data.datetime_iso || data.datetime, data.lang);
  if (dt) parts.push(T(dt, { align: 'center', h: 2 }));
  if (o.waiter) parts.push(T(`${L.waiter || 'Cameriere'}: ${o.waiter}`, { align: 'center', h: 2 }));
  parts.push(T(sep));
  (data.lines || []).forEach((it) => {
    parts.push(T(`${it.qty}x ${it.name}${it.variant ? ` (${it.variant})` : ''}`, { em: true, h: 3 }));
    (it.extras || []).forEach((e) => parts.push(T(`   + ${e.name}`, { h: 2 })));
    if (it.notes) parts.push(T(`   >> ${it.notes}`, { h: 2 }));
    // Più spazio tra un prodotto e l'altro.
    parts.push(`<feed line="1"/>`);
  });
  const body = parts.join('');
  return `<?xml version="1.0" encoding="utf-8"?>
<s:Envelope xmlns:s="http://schemas.xmlsoap.org/soap/envelope/"><s:Body>
<epos-print xmlns="${EPOS_NS}">
${body}
<feed line="${tailFeedLines(body)}"/>
<cut type="feed"/>
</epos-print>
</s:Body></s:Envelope>`;
}

/** XML di una semplice stampa di prova. */
export function buildEposTestXml(restaurantName = 'Restaurant Manager Pro', paperWidth = 80, logoXml = '') {
  const cols = colsFor(paperWidth);
  const sep = '-'.repeat(cols);
  const body = [
    logoXml ? logoXml + `<feed line="1"/>` : '',
    T(restaurantName, { align: 'center', em: true, dh: true }),
    T(sep),
    T('STAMPA DI PROVA', { align: 'center', em: true }),
    T('Epson ePOS-Print', { align: 'center' }),
    T(new Date().toLocaleString('it-IT'), { align: 'center' }),
    T(sep),
    T('Stampante collegata correttamente', { align: 'center' }),
  ].join('');
  return `<?xml version="1.0" encoding="utf-8"?>
<s:Envelope xmlns:s="http://schemas.xmlsoap.org/soap/envelope/"><s:Body>
<epos-print xmlns="${EPOS_NS}">
${body}
<feed line="3"/>
<cut type="feed"/>
</epos-print>
</s:Body></s:Envelope>`;
}

/** Costruisce l'URL del servizio ePOS-Print dalla configurazione. */
export function eposUrl(cfg) {
  return `${printerBaseUrl(cfg)}/cgi-bin/epos/service.cgi?devid=local_printer&timeout=10000`;
}

// Bit di stato ASB della risposta ePOS-Print (attributo "status"). Anche con
// success="true" la stampante puo' segnalare una condizione che impedisce la
// stampa fisica (coperchio aperto, carta finita, offline, ecc.).
const EPOS_STATUS_BITS = [
  { bit: 0x00000008, key: 'offline', label: 'Stampante offline' },
  { bit: 0x00000020, key: 'cover_open', label: 'Coperchio aperto' },
  { bit: 0x00000400, key: 'mechanical', label: 'Errore meccanico' },
  { bit: 0x00000800, key: 'cutter', label: 'Errore taglierina' },
  { bit: 0x00002000, key: 'unrecoverable', label: 'Errore non recuperabile' },
  { bit: 0x00004000, key: 'autorecover', label: 'Errore in recupero automatico' },
  { bit: 0x00080000, key: 'paper_end', label: 'Carta esaurita' },
];

/**
 * Interpreta la risposta XML della stampante ePOS-Print.
 * Ritorna { success, code, status, battery, problems[], offline }.
 */
export function parseEposResponse(txt) {
  const s = String(txt || '');
  const success = /success="true"/i.test(s);
  const codeM = /code="([^"]*)"/i.exec(s);
  const statusM = /status="(\d+)"/i.exec(s);
  const battM = /battery="(\d+)"/i.exec(s);
  const status = statusM ? Number(statusM[1]) : null;
  const battery = battM ? Number(battM[1]) : null;
  const problems = [];
  if (status != null) {
    for (const b of EPOS_STATUS_BITS) {
      if (status & b.bit) problems.push(b.label);
    }
  }
  return {
    success,
    code: codeM ? codeM[1] : '',
    status,
    battery,
    problems,
    offline: status != null ? !!(status & 0x00000008) : false,
  };
}

/** URL base della stampante (per aprire la pagina e accettare il certificato). */
export function printerBaseUrl(cfg) {
  const proto = (cfg.protocol === 'http') ? 'http' : 'https';
  const p = Number(cfg.port);
  // La porta 9100 e' la stampa RAW via socket (usata dall'agente/rete) e NON e'
  // una porta HTTP(S) valida per ePOS-Print: la ignoriamo cosi' si usa la porta
  // predefinita (443 per HTTPS, 80 per HTTP). Evita connessioni che falliscono.
  const port = (p > 0 && p !== 9100) ? `:${p}` : '';
  return `${proto}://${cfg.host}${port}`;
}

// Pausa asincrona di durata in millisecondi.
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

// Esegue una fetch con timeout esplicito tramite AbortController.
// Le stampanti a batteria (es. TM-P80II) possono impiegare alcuni secondi a
// "svegliarsi" dallo standby Wi-Fi: un timeout evita attese infinite e permette
// di ritentare in modo controllato.
async function fetchWithTimeout(url, options, timeoutMs) {
  const controller = new AbortController();
  const id = setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await fetch(url, { ...options, signal: controller.signal });
  } finally {
    clearTimeout(id);
  }
}

/**
 * Invia un XML ePOS-Print alla stampante e interpreta la risposta.
 *
 * Implementa un meccanismo di "risveglio + retry": se la stampante non risponde
 * (errore di rete o timeout) ritenta automaticamente fino a 3 volte con una
 * pausa di 2 secondi tra i tentativi. Questo dà tempo alle stampanti a batteria
 * (es. Epson TM-P80II) di uscire dallo standby Wi-Fi senza far fallire la stampa.
 *
 * NB: un errore "applicativo" (stampante raggiunta ma che rifiuta la stampa, es.
 * carta finita) NON viene ritentato perché ripetere non risolverebbe.
 */
export async function sendEpos(cfg, xml, opts = {}) {
  if (!cfg || !cfg.host) throw new Error('IP stampante non configurato');
  const url = eposUrl(cfg);
  const maxAttempts = Math.max(1, opts.retries != null ? opts.retries : 3);
  const delayMs = opts.delayMs != null ? opts.delayMs : 2000;
  const timeoutMs = opts.timeoutMs != null ? opts.timeoutMs : 8000;

  let lastNetworkError = null;

  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    let res;
    try {
      res = await fetchWithTimeout(url, {
        method: 'POST',
        headers: { 'Content-Type': 'text/xml; charset=utf-8' },
        body: xml,
      }, timeoutMs);
    } catch (e) {
      // Errore di rete o timeout (stampante in standby / non raggiungibile).
      lastNetworkError = e;
      if (attempt < maxAttempts) {
        // Pausa prima di ritentare: la stampante potrebbe doversi svegliare.
        await sleep(delayMs);
        continue;
      }
      // Tutti i tentativi falliti: messaggio esplicativo per l'utente.
      // kind='network' -> il dispositivo NON raggiunge la stampante (rete/certificato
      // diversi). Chi chiama può usarlo per NON consumare i tentativi del job e per
      // mettere in cooldown questa stazione su questo dispositivo.
      const netErr = new Error(
        "Impossibile raggiungere la stampante dopo piu' tentativi. La stampante potrebbe essere in standby per il risparmio energetico: " +
        "disattiva il risparmio energetico dal pannello della stampante (apri https://" + cfg.host + " nel browser). " +
        "Verifica inoltre di essere connesso alla sua rete Wi-Fi e di aver accettato il certificato."
      );
      netErr.kind = 'network';
      throw netErr;
    }

    // La stampante ha risposto: interpretiamo l'esito (nessun retry oltre).
    const txt = await res.text().catch(() => '');
    if (!res.ok) {
      // HTTP non-OK: spesso certificato non accettato / servizio ePOS non pronto:
      // trattato come problema di raggiungibilità (network) per non bruciare i job.
      const httpErr = new Error(`Errore stampante (HTTP ${res.status})`);
      httpErr.kind = 'network';
      throw httpErr;
    }

    const info = parseEposResponse(txt);

    if (info.success) {
      // success="true" significa che il comando e' stato accettato, ma la
      // stampante puo' comunque segnalare una condizione che blocca la stampa
      // fisica (coperchio, carta, offline). In tal caso avvisiamo l'utente.
      if (info.problems.length) {
        const pErr = new Error(
          `La stampante ha accettato la stampa ma segnala: ${info.problems.join(', ')}. ` +
          `Risolvi la condizione e riprova.`
        );
        pErr.kind = 'printer';
        throw pErr;
      }
      // Tutto regolare: ritorniamo le info diagnostiche (stato/batteria).
      return { ok: true, ...info };
    }

    // success="false": errore applicativo (NON si ritenta).
    const codeMap = {
      EPTR_COVER_OPEN: 'coperchio aperto',
      EPTR_REC_EMPTY: 'carta esaurita',
      EPTR_CUTTER: 'errore taglierina',
      EPTR_MECHANICAL: 'errore meccanico',
      EPTR_UNRECOVERABLE: 'errore non recuperabile',
      EPTR_AUTOMATICAL: 'errore in recupero automatico',
      SchemaError: 'XML non valido',
      DeviceNotFound: 'stampante non trovata (ID dispositivo errato)',
      PrintSystemError: 'errore del sistema di stampa',
      EX_BADPORT: 'porta/comunicazione non valida',
      EX_TIMEOUT: 'timeout della stampante',
    };
    const human = codeMap[info.code] || info.code;
    const rejErr = new Error(`La stampante ha rifiutato la stampa${human ? ` (${human})` : ''}`);
    rejErr.kind = 'printer';
    throw rejErr;
  }

  // Sicurezza: non dovrebbe arrivare qui, ma gestiamo comunque l'ultimo errore.
  const fallbackErr = new Error('Impossibile raggiungere la stampante.');
  fallbackErr.kind = 'network';
  throw fallbackErr;
}

/** Stampa una ricevuta via ePOS-Print. */
export async function eposPrintReceipt(cfg, data) {
  const logoSrc = data?.template?.logo_print_base64 || data?.template?.logo_base64;
  const logoXml = await logoToEposXml(logoSrc, cfg.paper_width);
  const xml = buildEposXml(data, cfg.paper_width, logoXml);
  return sendEpos(cfg, xml);
}

/** Stampa di prova via ePOS-Print. */
export async function eposTestPrint(cfg, restaurantName, logoSrc) {
  const logoXml = await logoToEposXml(logoSrc, cfg.paper_width);
  const xml = buildEposTestXml(restaurantName, cfg.paper_width, logoXml);
  return sendEpos(cfg, xml);
}

/** Stampa una comanda di reparto via ePOS-Print (stampante Wi-Fi/Ethernet della stazione).
 * Le comande usano timeout e retry PIÙ BREVI rispetto alle ricevute: la stampante di
 * postazione è tipicamente fissa (alimentata a rete), quindi non serve il lungo
 * "risveglio dallo standby". Un fallimento rapido permette a un altro dispositivo
 * (sulla rete giusta) di prendere e stampare subito la comanda. */
export async function eposPrintComanda(cfg, data, opts = {}) {
  const xml = buildComandaEposXml(data, cfg.paper_width);
  return sendEpos(cfg, xml, { retries: 2, delayMs: 700, timeoutMs: 4000, ...opts });
}

/** Apre SOLO il cassetto (nessuna stampa) via ePOS-Print, su entrambi i pin. */
export async function eposOpenDrawer(cfg) {
  const xml = `<?xml version="1.0" encoding="utf-8"?>
<s:Envelope xmlns:s="http://schemas.xmlsoap.org/soap/envelope/"><s:Body>
<epos-print xmlns="${EPOS_NS}">
<pulse drawer="drawer_1" time="pulse_100"/>
<pulse drawer="drawer_2" time="pulse_100"/>
</epos-print>
</s:Body></s:Envelope>`;
  return sendEpos(cfg, xml);
}
